import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translateText } from '../services/translationService';
import styles from './TranslateToggle.module.css';

function TranslateToggle({ text, className = '', onTranslated }) {
  const { t, i18n } = useTranslation();
  const [translatedText, setTranslatedText] = useState('');
  const [showTranslation, setShowTranslation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const hasText = Boolean(text && text.trim().length > 0);
  const targetLang = useMemo(() => (i18n.language || 'en').split('-')[0], [i18n.language]);
  const locale = i18n.language || 'en';

  useEffect(() => {
    setTranslatedText('');
    setShowTranslation(false);
    setError('');
  }, [targetLang, text]);

  const languageLabel = useMemo(() => {
    try {
      if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames !== 'undefined') {
        const displayNames = new Intl.DisplayNames([locale], { type: 'language' });
        return displayNames.of(targetLang) || targetLang;
      }
    } catch (_) {
      // ignore
    }
    return targetLang;
  }, [locale, targetLang]);

  if (!hasText) {
    return null;
  }

  const handleTranslateClick = async () => {
    if (translatedText) {
      setShowTranslation(prev => !prev);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const translation = await translateText(text, targetLang);
      setTranslatedText(translation);
      setShowTranslation(true);
      if (onTranslated) onTranslated();
    } catch (err) {
      setError(t('translation.error'));
    } finally {
      setLoading(false);
    }
  };

  const buttonLabel = translatedText
    ? (showTranslation ? t('translation.showOriginal') : t('translation.showTranslation'))
    : (loading ? t('translation.translating') : t('translation.translate'));

  return (
    <div className={`${styles.container} ${className}`}>
      <button
        type="button"
        className={styles.button}
        onClick={handleTranslateClick}
        disabled={loading}
      >
        {buttonLabel}
      </button>
      {error && <p className={styles.error}>{error}</p>}
      {translatedText && showTranslation && (
        <div className={styles.translationBox}>
          <p className={styles.translationLabel}>{t('translation.translatedLabel', { language: languageLabel })}</p>
          <p className={styles.translationText}>{translatedText}</p>
        </div>
      )}
    </div>
  );
}

export default TranslateToggle;
